import { Box, Group, Table, Text } from '@mantine/core';
import type { ReactElement } from 'react';
import InitialAvatar from '@/design/InitialAvatar';
import StatBand from '@/design/StatBand';
import { SurfaceCard } from '@/design/SurfaceCard';
import { formatCompact } from '@/shared/analytics/MetricFormatting';
import type { TeamOverview } from './InsightsTypes';

interface InsightsTeamSectionProps {
    team: TeamOverview;
}

/** Team view — how each direct report is doing, ranked by AI fluency. */
export default function InsightsTeamSection({ team }: InsightsTeamSectionProps): ReactElement {
    if (team.memberCount === 0) {
        return (
            <SurfaceCard $padding="lg">
                <Text c="dimmed">{team.summary}</Text>
            </SurfaceCard>
        );
    }
    return (
        <Box>
            <SurfaceCard $padding="md">
                <Text size="sm" mb="sm" style={{ lineHeight: 1.55 }}>
                    {team.summary}
                </Text>
                <StatBand
                    stats={[
                        { label: 'Team members', value: `${team.memberCount}` },
                        { label: 'Avg fluency', value: `${team.avgFluencyScore}`, hint: 'out of 100' },
                        { label: 'Avg active days', value: `${team.avgActiveDays}` },
                        { label: 'Total tokens', value: formatCompact(team.totalTokens) },
                    ]}
                />
            </SurfaceCard>
            <SurfaceCard $padding="md" style={{ marginTop: 'var(--mantine-spacing-md)' }}>
                <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb="sm" style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                    How each of your reports is doing
                </Text>
                <Table.ScrollContainer minWidth={680}>
                    <Table striped highlightOnHover verticalSpacing="sm">
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Person</Table.Th>
                                <Table.Th>Fluency</Table.Th>
                                <Table.Th>Active days</Table.Th>
                                <Table.Th>Sessions</Table.Th>
                                <Table.Th>Tokens</Table.Th>
                                <Table.Th>One-shot</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {team.members.map((member) => (
                                <Table.Tr key={member.userId}>
                                    <Table.Td>
                                        <Group gap="xs" wrap="nowrap">
                                            <InitialAvatar source={member.displayName} size={26} />
                                            <Box>
                                                <Text size="sm" fw={600}>
                                                    {member.displayName}
                                                </Text>
                                                <Text size="xs" c="dimmed">
                                                    {member.title || member.department || '—'}
                                                </Text>
                                            </Box>
                                        </Group>
                                    </Table.Td>
                                    <Table.Td>
                                        <Text ff="monospace" fw={700}>
                                            {member.fluencyScore}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>{member.activeDays}</Table.Td>
                                    <Table.Td>{member.sessions}</Table.Td>
                                    <Table.Td>{formatCompact(member.totalTokens)}</Table.Td>
                                    <Table.Td>{member.oneShotRate}%</Table.Td>
                                </Table.Tr>
                            ))}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            </SurfaceCard>
        </Box>
    );
}
